import { DeleteImpactDialog } from '../../../shared/components/common';
import { useBoardDeletionImpact, useDeleteBoard } from '../hooks/useBoards';

interface BoardDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  /** The board to delete (id + display name). Null while none is selected. */
  board: { id: string; name: string } | null;
  /** Called after a successful delete — e.g. to navigate away from a detail page. */
  onDeleted?: () => void;
}

/**
 * Board delete dialog built on the shared {@link DeleteImpactDialog}
 * (docs/platform/deletion-and-cascade-design.md §5.3). Surfaces the existing
 * keep-vs-delete-tasks decision as the `delete_tasks` option and blocks the
 * default board, replacing the ad-hoc ConfirmDialog + RadioGroup previously
 * duplicated across the board views.
 */
export function BoardDeleteDialog({ open, onClose, board, onDeleted }: BoardDeleteDialogProps) {
  const impactQuery = useBoardDeletionImpact(open ? board?.id ?? null : null);
  const deleteBoard = useDeleteBoard();

  const handleConfirm = async (options: Record<string, boolean>) => {
    if (!board) return;
    await deleteBoard.mutateAsync({ id: board.id, deleteItems: options.delete_tasks === true });
    onClose();
    onDeleted?.();
  };

  if (!board) return null;

  return (
    <DeleteImpactDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      impact={impactQuery.data}
      isLoadingImpact={impactQuery.isLoading}
      isImpactError={impactQuery.isError}
      isDeleting={deleteBoard.isPending}
      entityLabel={board.name}
      keyPrefix="tasks:boardDelete"
    />
  );
}
